import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Music, Search, ExternalLink, Mic2, Settings, ListMusic } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import { useCollection } from '../../hooks/useFirestore'
import { formatDate, todayISO } from '../../utils/helpers'
import { Badge, Card, Button, Modal, Input } from '../../components/common'

// Shared song detail modal: lyrics, audio player, links
export const SongDetailModal = ({ song, onClose }) => (
  <Modal isOpen={!!song} onClose={onClose} title={song?.title || 'Song'} size="lg">
    {song && (
      <div className="space-y-4">
        <div className="flex items-center gap-2 flex-wrap text-sm text-gray-600">
          {song.artist && <span>{song.artist}</span>}
          {song.songKey && <Badge variant="secondary" size="sm">Key: {song.songKey}</Badge>}
        </div>
        {song.youtubeUrl && (
          <a
            href={song.youtubeUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-primary-600 hover:text-primary-700 font-medium"
          >
            Listen on YouTube <ExternalLink className="w-4 h-4" />
          </a>
        )}
        {song.audioUrl && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Practice recording</p>
            <audio controls src={song.audioUrl} className="w-full" preload="none" />
          </div>
        )}
        {song.lyrics && (
          <div>
            <p className="text-sm font-medium text-gray-700 mb-1">Lyrics</p>
            <pre className="whitespace-pre-wrap font-body text-sm text-gray-700 bg-gray-50 rounded-lg p-4 max-h-96 overflow-y-auto">{song.lyrics}</pre>
          </div>
        )}
        {song.notes && <p className="text-sm text-gray-500">{song.notes}</p>}
      </div>
    )}
  </Modal>
)

export const SetlistCard = ({ setlist, songsById, onOpenSong }) => (
  <div className="bg-white rounded-xl shadow-soft overflow-hidden">
    <div className="px-5 py-3 bg-gray-50 border-b border-gray-100 flex items-center justify-between gap-2 flex-wrap">
      <p className="font-heading font-semibold text-dark-100">
        {formatDate(setlist.serviceDate, 'EEEE, MMMM d, yyyy')}
        {setlist.title ? ` — ${setlist.title}` : ''}
      </p>
      <Badge variant="primary" size="sm">{setlist.departmentName || setlist.departmentId}</Badge>
    </div>
    <div className="divide-y divide-gray-100">
      {(setlist.items || []).map((item, i) => {
        const song = songsById[item.songId]
        return (
          <div key={`${item.songId}-${i}`} className="px-5 py-3 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-medium text-dark-100">
                <span className="text-gray-400 mr-2">{i + 1}.</span>
                {item.title}
                {item.songKey && <span className="text-sm text-gray-500 ml-2">({item.songKey})</span>}
              </p>
              <div className="flex items-center gap-2 flex-wrap mt-0.5">
                {item.leadName && (
                  <Badge variant="warning" size="sm" icon={Mic2}>
                    Lead: {item.leadName}
                  </Badge>
                )}
                {item.notes && <span className="text-sm text-gray-500">{item.notes}</span>}
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {item.youtubeUrl && (
                <a
                  href={item.youtubeUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-primary-600 hover:bg-primary-50 rounded-lg"
                  title="Listen"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>
              )}
              {song && (
                <button
                  onClick={() => onOpenSong(song)}
                  className="px-3 py-1.5 text-sm text-primary-600 hover:bg-primary-50 rounded-lg font-medium"
                >
                  Details
                </button>
              )}
            </div>
          </div>
        )
      })}
    </div>
  </div>
)

const WorkersSongs = () => {
  const { user, userProfile, isEditor } = useAuth()
  const [tab, setTab] = useState('upcoming')
  const [search, setSearch] = useState('')
  const [openSong, setOpenSong] = useState(null)

  const { data: departments } = useCollection('departments', {
    orderByField: 'order',
    orderDirection: 'asc',
  })

  // Staff see every department's songs; workers see their own departments
  const myDepartments = useMemo(() => {
    const own = userProfile?.departmentIds || []
    const scope = isEditor ? (departments || []).map((d) => d.id) : own
    return scope.length > 0 ? scope : ['__none__']
  }, [userProfile, isEditor, departments])

  const isLeader =
    isEditor ||
    (departments || []).some((d) => (d.leaderUids || []).includes(user?.uid))

  const today = todayISO()

  const { data: upcoming, loading: loadingUpcoming } = useCollection('setlists', {
    orderByField: 'serviceDate',
    orderDirection: 'asc',
    whereClauses: [
      { field: 'departmentId', operator: 'in', value: myDepartments },
      { field: 'status', operator: '==', value: 'published' },
      { field: 'serviceDate', operator: '>=', value: today },
    ],
  })

  const { data: past } = useCollection('setlists', {
    orderByField: 'serviceDate',
    orderDirection: 'desc',
    limitCount: 10,
    whereClauses: [
      { field: 'departmentId', operator: 'in', value: myDepartments },
      { field: 'status', operator: '==', value: 'published' },
      { field: 'serviceDate', operator: '<', value: today },
    ],
  })

  const { data: songs, loading: loadingSongs } = useCollection('songs', {
    orderByField: 'title',
    orderDirection: 'asc',
    whereClauses: [{ field: 'departmentId', operator: 'in', value: myDepartments }],
  })

  const songsById = useMemo(
    () => Object.fromEntries((songs || []).map((s) => [s.id, s])),
    [songs]
  )

  const deptName = (slug) => departments?.find((d) => d.id === slug)?.name || slug

  const filteredSongs = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return songs || []
    return (songs || []).filter(
      (s) =>
        s.title?.toLowerCase().includes(q) ||
        s.artist?.toLowerCase().includes(q)
    )
  }, [songs, search])

  const list = tab === 'upcoming' ? upcoming : past

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-heading font-bold text-dark-100">Songs</h1>
          <p className="text-gray-500">Song lists and the library for your departments</p>
        </div>
        {isLeader && (
          <Link to="/workers/songs/manage">
            <Button variant="primary" icon={Settings}>Manage</Button>
          </Link>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 overflow-x-auto">
        {[
          { key: 'upcoming', label: 'Upcoming' },
          { key: 'past', label: 'Past' },
          { key: 'library', label: 'Song Library' },
        ].map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              tab === t.key
                ? 'bg-primary-900 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'library' ? (
        <div className="space-y-3">
          <Input
            icon={Search}
            placeholder="Search by title or artist..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <div className="space-y-2">
            {filteredSongs.map((song) => (
              <button
                key={song.id}
                onClick={() => setOpenSong(song)}
                className="w-full text-left bg-white rounded-xl shadow-soft p-4 flex items-center justify-between gap-3 hover:shadow-lg transition-shadow"
              >
                <div className="min-w-0">
                  <p className="font-medium text-dark-100">{song.title}</p>
                  <p className="text-sm text-gray-500">
                    {[song.artist, song.songKey ? `Key: ${song.songKey}` : null]
                      .filter(Boolean)
                      .join(' · ')}
                  </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  {song.audioUrl && <Badge variant="info" size="sm">Audio</Badge>}
                  <Badge variant="primary" size="sm">{deptName(song.departmentId)}</Badge>
                </div>
              </button>
            ))}
            {!loadingSongs && filteredSongs.length === 0 && (
              <Card className="text-center py-12 text-gray-500">
                <Music className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>{search ? 'No songs match your search' : 'No songs in the library yet'}</p>
              </Card>
            )}
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          {(list || []).map((setlist) => (
            <SetlistCard
              key={setlist.id}
              setlist={{ ...setlist, departmentName: deptName(setlist.departmentId) }}
              songsById={songsById}
              onOpenSong={setOpenSong}
            />
          ))}
          {!loadingUpcoming && (list || []).length === 0 && (
            <Card className="text-center py-12 text-gray-500">
              <ListMusic className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No {tab} song lists</p>
            </Card>
          )}
        </div>
      )}

      <SongDetailModal song={openSong} onClose={() => setOpenSong(null)} />
    </div>
  )
}

export default WorkersSongs
